import {sendAnalyticsEvent} from '#/analytics/analytics';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {AddFundsButton} from '#/components/add-funds/add-funds-button.tsx';
import {MenuButton} from '#/components/buttons/menu-button';
import {ShareLandLogoLink} from '#/components/header/share-land-logo-link';
import {SocialLinks} from '#/components/links/social-links';
import {ClientEnabledFeaturesContext} from '#/contexts/client-enabled-features-provider';
import {ModalContext} from '#/contexts/modal-provider';
import {UIStatusContext} from '#/contexts/ui-status-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import environment from '#/environment/environment';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {PortfolioTab} from '#/navigation/tabs';
import {ExchangeRoute} from '#/routes/exchange/exchange-route';
import {getExchangeRouteWithQueryParams} from '#/routes/exchange/get-exchange-route-with-query-params';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {
  ArrowDownIcon,
  ArrowLeftOnRectangleIcon,
  ArrowUpIcon,
  ChatBubbleLeftIcon,
  HomeIcon,
  MapIcon,
  PlusIcon,
  UserIcon,
} from '@heroicons/react/24/solid';
import {motion} from 'framer-motion';
import Cookies from 'js-cookie';
import {FunctionComponent, useContext, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useMatch} from 'react-router-dom';
import {useClickAway, useMedia} from 'react-use';

const MenuBox: FunctionComponent<DefaultChildren> = () => {
  // Context
  const clientEnabledFeatures = useContext(ClientEnabledFeaturesContext);
  const {toggleMenu} = useContext(UIStatusContext);
  const {modals} = useContext(ModalContext);
  const {
    accountResult: {isConnected},
  } = useContext(WalletContext);

  // External hooks
  const {t} = useTranslation('translation');
  const isMinWidth = {
    sm: useMedia('(min-width: 640px)'),
  };

  // Internal hooks
  const ref = useRef(null);

  useClickAway(ref, () => {
    if (isMinWidth.sm && modals.length === 0) {
      toggleMenu(false);
    }
  });

  return (
    <motion.div
      ref={ref}
      initial={{backgroundColor: 'rgba(0, 0, 0, 0)'}}
      animate={{backgroundColor: 'rgba(0, 0, 0, 0.3)'}}
      exit={{backgroundColor: 'rgba(0, 0, 0, 0)'}}
      transition={{ease: 'easeInOut', duration: 0.2}}
      className='absolute top-0 left-0 w-full h-full z-30 pointer-events-none'
    >
      <motion.div
        ref={ref}
        initial={{x: '-100%'}}
        animate={{x: '0'}}
        exit={{x: '-100%'}}
        transition={{ease: 'easeInOut', duration: 0.2}}
        className='w-full sm:w-menuWidth h-full flex flex-col absolute z-20 shadow-2xl bg-graybluedark3 sm:bg-grayblue pointer-events-auto lg:rounded-r-2xl'
      >
        <div className={'h-header flex items-center gap-4 px-5 py-3'}>
          <MenuButton mode='close' />
          <ShareLandLogoLink />
        </div>

        <div className='grow flex flex-col justify-between text-sm h-full p-6 pt-3 overflow-hidden'>
          <div className='flex flex-col gap-4 pb-2 py-0 overflow-y-scroll'>
            <SectionTitle>{t('components.menu.sections.title')}</SectionTitle>

            <MenuItemLink path={ExchangeRoute.HOME} IconComponent={HomeIcon}>
              {t('components.menu.sections.explore')}
            </MenuItemLink>

            <MenuItemLink path={ExchangeRoute.MAP} IconComponent={MapIcon}>
              {t('components.menu.sections.map')}
            </MenuItemLink>

            <MenuItemLink
              path={ExchangeRoute.PORTFOLIO}
              to={getExchangeRouteWithQueryParams(ExchangeRoute.PORTFOLIO, {portfolioTabId: PortfolioTab.ASSETS})}
              IconComponent={UserIcon}
            >
              {t('components.menu.sections.portfolio')}
            </MenuItemLink>
            <MenuItemLink
              path={ExchangeRoute.HOW_IT_WORKS_NOTION}
              to={ExchangeRoute.HOW_IT_WORKS_NOTION}
              IconComponent={ChatBubbleLeftIcon}
              target='_blank'
            >
              {t('pages.landing.howItWorks.title')}
            </MenuItemLink>

            <VerticalSeparator my='my-0' />

            <SectionTitle>{t('components.menu.actions.title')}</SectionTitle>

            {isConnected && (
              <>
                {!environment.isWaitlistNet && (
                  <>
                    <MenuItemAddNativeTokensButton />
                    {clientEnabledFeatures.transferWallet && <MenuItemTransferWalletButton />}
                    {clientEnabledFeatures.importWallet && <MenuItemImportWalletButton />}
                  </>
                )}

                <MenuItemLogoutButton />
              </>
            )}
          </div>

          <div className='w-full mt-6'>
            {environment.isWaitlistNet && <AddFundsButton className='w-full mb-6 justify-center' />}
            <div className='w-full flex justify-center gap-2 flex-wrap text-gray-400 opacity-50 text-xs'>
              <SocialLinks />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MenuItemAddNativeTokensButton: FunctionComponent = () => {
  const {open} = useContext(ModalContext);
  const {t} = useTranslation('translation');

  const onClick = () => {
    open({name: 'AddNativeTokensModal', props: {automatic: false}});
  };

  return (
    <button onClick={onClick} className='cursor-pointer'>
      <MenuIconText IconComponent={PlusIcon} selected={false}>
        {t('components.menu.actions.addNativeTokens')}
      </MenuIconText>
    </button>
  );
};

const MenuItemTransferWalletButton: FunctionComponent = () => {
  const {open} = useContext(ModalContext);
  const {t} = useTranslation('translation');

  const onClick = () => {
    open({name: 'WalletTransferModal', props: {}});
  };

  return (
    <button onClick={onClick} className='cursor-pointer'>
      <MenuIconText IconComponent={ArrowUpIcon} selected={false}>
        {t('components.menu.actions.transferWallet')}
      </MenuIconText>
    </button>
  );
};

const MenuItemImportWalletButton: FunctionComponent = () => {
  const {open} = useContext(ModalContext);
  const {t} = useTranslation('translation');

  const onClick = () => {
    open({name: 'WalletImportModal', props: {}});
  };

  return (
    <button onClick={onClick} className='cursor-pointer'>
      <MenuIconText IconComponent={ArrowDownIcon} selected={false}>
        {t('components.menu.actions.importWallet')}
      </MenuIconText>
    </button>
  );
};

const MenuItemLogoutButton: FunctionComponent = () => {
  const {toggleMenu} = useContext(UIStatusContext);
  const {logout} = useContext(WalletContext);
  const {t} = useTranslation('translation');

  const {actionMenu} = useQueryNavigationActions();
  const onClick = async () => {
    logout();
    actionMenu.close();
    toggleMenu(false);
    sendAnalyticsEvent.logout();
    Cookies.remove('referrer');
  };
  return (
    <button onClick={onClick} className='cursor-pointer'>
      <MenuIconText IconComponent={ArrowLeftOnRectangleIcon} selected={false}>
        {t('components.menu.actions.logout')}
      </MenuIconText>
    </button>
  );
};

const SectionTitle: FunctionComponent<DefaultChildren> = ({children}) => (
  <span className='text-gray-400 font-semibold pointer-events-none select-none'>{children}</span>
);

const MenuIconText: FunctionComponent<
  {
    IconComponent: FunctionComponent<ClassName>;
    selected?: boolean;
  } & ClassName &
    DefaultChildren
> = ({IconComponent, children, selected = false}) => {
  return (
    <div
      className={
        'flex items-center gap-2 group/menu-item font-semibold data-[selected=true]:text-white data-[selected=false]:text-gray-400 data-[selected=false]:hover:text-blue-600'
      }
      data-selected={selected}
    >
      <IconComponent
        className={
          'w-4 h-4 stroke-transparent group/menu-item-data-[selected=true]:fill-white group/menu-item-data-[selected=false]:fill-gray-400 group/menu-item-data-[selected=false]:group-hover/menu-item:fill-deepblue'
        }
      />
      {children}
    </div>
  );
};

const MenuItemLink: FunctionComponent<
  {
    path: string;
    to?: string;
    IconComponent: FunctionComponent<ClassName>;
    target?: string;
  } & DefaultChildren
> = ({path, to = path, IconComponent, children, ...props}) => {
  const match = useMatch(path);

  return (
    <Link to={to} {...props}>
      <MenuIconText IconComponent={IconComponent} selected={!!match}>
        {children}
      </MenuIconText>
    </Link>
  );
};

export default MenuBox;
